import React from 'react';

import { SUPPORTED_LANGUAGES_COUNT } from 'src/data/generation/const';
// DO NOT REMOVE! Without the following imports getWorkspacesByTier and getSeatsByTier is trying to be imported from const, resulting in an error
import { getWorkspacesByTier, getSeatsLabelByTier } from 'src/data/subscription/subscriptionTier';

import { CheckmarkIcon } from 'src/assets/icons/Checkmark';
import {
  SUBSCRIPTION_PLAN,
  DEFAULT_SUBSCRIPTION_PLAN,
  SUBSCRIPTION_BASIC_TIER,
  DEFAULT_BASIC_TIER,
  DETAILS_TABLE_ICONS,
  SUBSCRIPTION_DATA_DRIVEN_TIER,
  DEFAULT_DD_TIER,
  SUBSCRIPTION_PLAN_DEPRECATED,
  SUBSCRIPTION_DATA_DRIVEN_SEAT_TIER,
  SUBSCRIPTION_SEAT_TIER,
} from './const';

export const clampSubscriptionPlan = (plan, defaultValue = DEFAULT_SUBSCRIPTION_PLAN) => {
  const values = Object.values(SUBSCRIPTION_PLAN);
  if (values.includes(plan)) {
    return plan;
  }
  return defaultValue;
};

export const isSubscriptionPlanDeprecated = (plan) =>
  Object.values(SUBSCRIPTION_PLAN_DEPRECATED).includes(plan);

export const isStarterPlan = (plan) => plan === SUBSCRIPTION_PLAN.starter;
export const isBasicPlan = (plan) => plan === SUBSCRIPTION_PLAN.basic;
export const isDDTeamsPlan = (plan) => plan === SUBSCRIPTION_PLAN.dd_teams;

export const shouldUpgradeToBusiness = (plan) =>
  isStarterPlan(plan) || isBasicPlan(plan) || isDDTeamsPlan(plan);
export const isBusinessSelfServePlan = (plan) => plan === SUBSCRIPTION_PLAN.business_self_serve;
export const isEnterpriseOrAcePlan = (plan) =>
  [SUBSCRIPTION_PLAN.enterprise, SUBSCRIPTION_PLAN.business].includes(plan);
export const isSelfServicePlan = (plan) => {
  return ![SUBSCRIPTION_PLAN.enterprise, SUBSCRIPTION_PLAN.business].includes(plan);
};
export const isDataDrivenPlan = (plan) => isDDTeamsPlan(plan);

export const isStarterOrBasicPlan = (plan) => isStarterPlan(plan) || isBasicPlan(plan);

export const isBusinessPlan = (plan) => plan === SUBSCRIPTION_PLAN.business;
export const isSelfServeBusinessPlan = (plan) => plan === SUBSCRIPTION_PLAN.business_self_serve;

export const isPlanWithTiers = (plan) =>
  plan === SUBSCRIPTION_PLAN.dataDriven || plan === SUBSCRIPTION_PLAN.proAgency;

const FEATURE_DD_COPYWRITING_TOOLS = {
  text: '200+ Data-driven copywriting tools',
  tooltip:
    "Drive performance across your ads, social posts, landing pages, emails and more.\n\nAnyword's massive proprietary data sets let you create highly effective copy for any marketing channel.",
};

const FEATURE_REAL_TIME_PREDICTIVE_PERFORMANCE_SCORE = {
  text: 'Real-time predictive performance score and analytics',
  tooltip: 'Get real-time scoring and analytics for every copy edit you make.',
};

const FEATURE_REAL_TIME_SCORING_ANALYTICS = {
  text: 'Improve and boost scores for your copy in real time!',
  tooltip: 'Have Anyword rewrite any existing copy to improve its predictive performance score.',
};

const FEATURE_LANGUAGES = {
  text: `${SUPPORTED_LANGUAGES_COUNT} languages`,
};

const FEATURE_AI_TOOLS = { text: '100+ AI writing tools' };

const FEATURE_API_ACCESS = {
  text: 'API access',
};

const PLAN_PROPS = {
  [SUBSCRIPTION_PLAN.basic]: {
    label: 'Free',
    description:
      'Everything you need to write professional copy 10x faster. No time limit, no credit card required.',
    creditsPerMonth: '1,000',
    seats: '1',
    blogPosts: DETAILS_TABLE_ICONS.check,
    aiWritingTools: DETAILS_TABLE_ICONS.check,
    social: DETAILS_TABLE_ICONS.check,
    ads: DETAILS_TABLE_ICONS.check,
    landingPages: DETAILS_TABLE_ICONS.check,
    subjectLines: DETAILS_TABLE_ICONS.check,
    productListings: DETAILS_TABLE_ICONS.check,
    customAiModels: DETAILS_TABLE_ICONS.minus,
    importAdsFromSocial: DETAILS_TABLE_ICONS.minus,
    copyVariationsInformed: DETAILS_TABLE_ICONS.minus,
    apiAccess: DETAILS_TABLE_ICONS.minus,
    support: 'Chat and ticket\nsupport',
    policyComplianceDetection: DETAILS_TABLE_ICONS.minus,
    realtimeScoringAndAnalytics: DETAILS_TABLE_ICONS.minus,
    scoreOptimization: DETAILS_TABLE_ICONS.minus,
    features: [
      {
        text: '<b>1,000</b> word credits per month',
      },
      FEATURE_AI_TOOLS,
      FEATURE_DD_COPYWRITING_TOOLS,
      {
        text: 'Blog post wizard',
      },
      {
        text: 'US English supported',
      },
      {
        text: '1 seat',
        seatsLimitFeature: true,
      },
    ],
    featureGroups: [
      {
        features: [
          '100+ performance-driven templates',
          'Blog Wizard',
          '5 one-click boost performance',
          'Predictive performance scores & analytics',
          'High-performing talking point suggestions',
          '3 tones of voice',
          '5 target audiences',
          '25 brand rules',
          '10 messaging bank asset',
          '10 custom copywriting formula',
        ],
      },
    ],
  },
  [SUBSCRIPTION_PLAN.basicTiered]: {
    label: 'Basic',
    description: 'Everything you need to write professional copy 10x faster.',
    realtimeScoringAndAnalytics: DETAILS_TABLE_ICONS.minus,
    scoreOptimization: DETAILS_TABLE_ICONS.minus,
    creditsPerMonth: '20,000 — 300,000',
    seats: 'Multiple',
    blogPosts: DETAILS_TABLE_ICONS.check,
    aiWritingTools: DETAILS_TABLE_ICONS.check,
    social: DETAILS_TABLE_ICONS.check,
    ads: DETAILS_TABLE_ICONS.check,
    landingPages: DETAILS_TABLE_ICONS.check,
    subjectLines: DETAILS_TABLE_ICONS.check,
    productListings: DETAILS_TABLE_ICONS.check,
    customAiModels: DETAILS_TABLE_ICONS.minus,
    importAdsFromSocial: DETAILS_TABLE_ICONS.minus,
    copyVariationsInformed: DETAILS_TABLE_ICONS.minus,
    apiAccess: DETAILS_TABLE_ICONS.minus,
    support: 'Chat and ticket\nsupport',
    policyComplianceDetection: DETAILS_TABLE_ICONS.minus,
    features: [
      {
        text: '<b>{wordsLimit}</b> word credits per month',
        wordCreditsFeature: true,
      },
      FEATURE_AI_TOOLS,
      FEATURE_DD_COPYWRITING_TOOLS,
      {
        text: 'Blog post wizard',
      },
      FEATURE_LANGUAGES,
      {
        text: 'Multiple seats',
        seatsLimitFeature: true,
      },
    ],
    tiers: SUBSCRIPTION_BASIC_TIER,
    defaultTier: DEFAULT_BASIC_TIER,
  },
  [SUBSCRIPTION_PLAN.dataDriven]: {
    label: 'Data-Driven',
    description:
      'Everything you need to create on-brand content at scale that converts! With real-time scoring, analytics and optimizations!',
    realtimeScoringAndAnalytics: DETAILS_TABLE_ICONS.check,
    creditsPerMonth: '30,000 — Unlimited',
    scoreOptimization: DETAILS_TABLE_ICONS.check,
    seats: 'Multiple',
    blogPosts: DETAILS_TABLE_ICONS.check,
    aiWritingTools: DETAILS_TABLE_ICONS.check,
    social: DETAILS_TABLE_ICONS.check,
    ads: DETAILS_TABLE_ICONS.check,
    customAiModels: DETAILS_TABLE_ICONS.minus,
    importAdsFromSocial: DETAILS_TABLE_ICONS.minus,
    copyVariationsInformed: DETAILS_TABLE_ICONS.minus,
    apiAccess: DETAILS_TABLE_ICONS.minus,
    landingPages: 'Write from scratch',
    subjectLines: DETAILS_TABLE_ICONS.check,
    productListings: DETAILS_TABLE_ICONS.check,
    support: 'Chat and ticket\nsupport',
    policyComplianceDetection: DETAILS_TABLE_ICONS.minus,
    features: [
      {
        text: '<b>{wordsLimit}</b> word credits per month',
        wordCreditsFeature: true,
      },
      FEATURE_AI_TOOLS,
      FEATURE_DD_COPYWRITING_TOOLS,
      FEATURE_REAL_TIME_PREDICTIVE_PERFORMANCE_SCORE,
      FEATURE_REAL_TIME_SCORING_ANALYTICS,
      { text: 'Blog post wizard' },
      FEATURE_LANGUAGES,
      {
        text: 'Multiple seats',
        seatsLimitFeature: true,
      },
    ],
    tiers: SUBSCRIPTION_DATA_DRIVEN_TIER,
    defaultTier: DEFAULT_DD_TIER,
  },

  [SUBSCRIPTION_PLAN.professional]: {
    label: 'Professional',
    description:
      'Boost the performance of your copy across the marketing funnel with custom algorithms — trained on your actual performance data.',
    subtitle:
      'A must-have for growth marketers, paid media teams, content teams, social media teams, and demand generation teams.',
    numberOfMonthsDiscount: 4,
    creditsPerMonth: 'Unlimited',
    seats: 'Multiple',
    blogPosts: DETAILS_TABLE_ICONS.check,
    aiWritingTools: DETAILS_TABLE_ICONS.check,
    social: DETAILS_TABLE_ICONS.check,
    ads: DETAILS_TABLE_ICONS.check,
    landingPages: 'Write from scratch',
    subjectLines: DETAILS_TABLE_ICONS.check,
    productListings: DETAILS_TABLE_ICONS.check,
    customAiModels: 'Ads, Emails',
    support: '<span>Dedicated customer\nsuccess manager</span>',
    importAdsFromSocial: DETAILS_TABLE_ICONS.check,
    copyVariationsInformed: DETAILS_TABLE_ICONS.check,
    apiAccess: DETAILS_TABLE_ICONS.check,
    policyComplianceDetection: 'Facebook, Google',
    realtimeScoringAndAnalytics: DETAILS_TABLE_ICONS.check,
    scoreOptimization: DETAILS_TABLE_ICONS.check,
    features: [
      {
        text: 'Leverage copy algorithms custom built for your audiences and products. Increase conversions and engagement with better copy generations, and highly accurate copy predictions',
      },
      {
        text: 'Workspaces for every brand and pod in your organization. Includes role-based permissioning, data security, and multi-tenancy',
      },
      {
        text: 'Integrate with 3rd party platforms to support your existing workflows',
      },
      { text: 'Create automations to craft any copy tailored to your specifications at scale' },
      {
        text: "Leverage data sources such as customers' reviews to inform your custom copy algorithm",
      },

      { text: 'Discounted word credit prices to match your scale needs' },
    ],
  },

  [SUBSCRIPTION_PLAN.website]: {
    label: 'Website',
    description:
      'Connect Anyword to your website and serve every visitor an optimized message based on their intent.',
    subtitle: 'Ideal for digital customer experience, web, and demand generation teams.',
    numberOfMonthsDiscount: 4,
    creditsPerMonth: 'Unlimited',
    seats: 'Multiple',
    blogPosts: DETAILS_TABLE_ICONS.check,
    aiWritingTools: DETAILS_TABLE_ICONS.check,
    social: DETAILS_TABLE_ICONS.check,
    ads: DETAILS_TABLE_ICONS.check,
    landingPages: 'Improve your existing\nlanding pages continuously\nor write from scratch',
    subjectLines: DETAILS_TABLE_ICONS.check,
    productListings: DETAILS_TABLE_ICONS.check,
    support: '<span>Dedicated customer\nsuccess manager</span>',
    customAiModels: DETAILS_TABLE_ICONS.minus,
    importAdsFromSocial: DETAILS_TABLE_ICONS.minus,
    copyVariationsInformed: DETAILS_TABLE_ICONS.minus,
    apiAccess: DETAILS_TABLE_ICONS.minus,
    policyComplianceDetection: DETAILS_TABLE_ICONS.minus,
    realtimeScoringAndAnalytics: DETAILS_TABLE_ICONS.check,
    scoreOptimization: DETAILS_TABLE_ICONS.check,
    features: [
      { text: 'Generate copy at scale that outperforms your existing website copy' },
      { text: 'Leverage landing page and website-specific data-driven copy tools' },
      { text: 'Optimize copy across your website in real-time based on referer source and UTM' },
      { text: 'Connect target audiences and cohorts to sets of personalized web copy' },
      {
        text: 'Connect your website copy to your ad copy, aligning them and continously optimizing both',
      },
      { text: 'Integrate and get started in seconds' },
      { text: 'Test many copy variations simultaneously, both your own and AI created' },
    ],
  },

  [SUBSCRIPTION_PLAN.enterprise]: {
    label: 'Enterprise',
    description:
      'Leverage your historical data and brand guidelines to create content that works for your specific audience.',
    price: 999,
    creditsPerMonth: 'Unlimited',
    seats: 'Custom',
    blogPosts: DETAILS_TABLE_ICONS.check,
    aiWritingTools: DETAILS_TABLE_ICONS.check,
    social: DETAILS_TABLE_ICONS.check,
    ads: DETAILS_TABLE_ICONS.check,
    landingPages: 'Write from scratch or\nimprove your existing landing\npages continuously',
    subjectLines: DETAILS_TABLE_ICONS.check,
    productListings: DETAILS_TABLE_ICONS.check,
    support: 'Dedicated customer\nsuccess manager',
    policyComplianceDetection: DETAILS_TABLE_ICONS.minus,
    customAiModels: DETAILS_TABLE_ICONS.minus,
    importAdsFromSocial: DETAILS_TABLE_ICONS.minus,
    apiAccess: DETAILS_TABLE_ICONS.minus,
    features: [
      {
        text: 'Everything in Data-Driven Unlimited, plus:',
        isHeader: true,
      },
      FEATURE_API_ACCESS,
      {
        text: 'Brand guidelines filter',
      },
      {
        text: 'Custom models',
      },
      {
        text: 'Enterprise-grade services & integrations',
      },
      {
        text: 'Custom number of seats',
      },
    ],
  },

  [SUBSCRIPTION_PLAN.starter]: {
    classNamePrefix: 'PricingStarter',
    label: 'Starter',
    description:
      'For solo marketers & freelancers who want on-brand content backed by Anyword’s performance data.',
    realtimeScoringAndAnalytics: DETAILS_TABLE_ICONS.minus,
    scoreOptimization: DETAILS_TABLE_ICONS.minus,
    seats: 1,
    price: 49,
    priceAnnually: 468,
    priceAnnuallyPerMonth: 39,
    percentOfAnnualDiscount: 20,
    blogPosts: DETAILS_TABLE_ICONS.check,
    aiWritingTools: DETAILS_TABLE_ICONS.check,
    social: DETAILS_TABLE_ICONS.check,
    ads: DETAILS_TABLE_ICONS.check,
    landingPages: DETAILS_TABLE_ICONS.check,
    subjectLines: DETAILS_TABLE_ICONS.check,
    productListings: DETAILS_TABLE_ICONS.check,
    customAiModels: DETAILS_TABLE_ICONS.minus,
    importAdsFromSocial: DETAILS_TABLE_ICONS.minus,
    copyVariationsInformed: DETAILS_TABLE_ICONS.minus,
    apiAccess: DETAILS_TABLE_ICONS.minus,
    support: 'Chat and ticket\nsupport',
    policyComplianceDetection: DETAILS_TABLE_ICONS.minus,
    features: [
      { text: '5 one-click Boost Performance' },
      { text: '100+ performance-driven templates' },
      { text: 'Blog wizard' },
      { text: 'Predictive performance scores & analytics' },
      { text: 'High-performing talking point suggestions' },
      { text: '3 tones of voice' },
      { text: '5 target audiences' },
      { text: '10 messaging bank assets' },
      { text: '10 custom copywriting formulas' },
      { text: '25 brand rules' },
      { text: '1 Workspace' },
    ],
    featuresTitle: 'Starter plan includes',
    featureGroups: [
      {
        features: [
          '1 Seat',
          '100+ marketing templates',
          'Unlimited copy generation',
          'Predictive performance scores & analytics',
          'Blog Wizard with plagiarism checker',
          'Train AI on your brand voice',
          'High-performing talking points',
          'Use Anyword on ChatGPT, Google Docs,\n Notion & more with Google Chrome extension',
        ],
      },
    ],
  },
  [SUBSCRIPTION_PLAN.dd_teams]: {
    classNamePrefix: 'PricingDD',
    label: 'Data-Driven',
    description:
      'For marketing teams who want on-brand content backed by Anyword’s\nperformance data.',
    realtimeScoringAndAnalytics: DETAILS_TABLE_ICONS.check,
    creditsPerMonth: 'Unlimited',
    scoreOptimization: DETAILS_TABLE_ICONS.check,
    seats: 'Multiple',
    blogPosts: DETAILS_TABLE_ICONS.check,
    aiWritingTools: DETAILS_TABLE_ICONS.check,
    social: DETAILS_TABLE_ICONS.check,
    ads: DETAILS_TABLE_ICONS.check,
    customAiModels: DETAILS_TABLE_ICONS.minus,
    importAdsFromSocial: DETAILS_TABLE_ICONS.minus,
    copyVariationsInformed: DETAILS_TABLE_ICONS.minus,
    apiAccess: DETAILS_TABLE_ICONS.minus,
    landingPages: 'Write from scratch',
    subjectLines: DETAILS_TABLE_ICONS.check,
    productListings: DETAILS_TABLE_ICONS.check,
    support: 'Chat and ticket\nsupport',
    policyComplianceDetection: DETAILS_TABLE_ICONS.minus,
    tiers: SUBSCRIPTION_DATA_DRIVEN_SEAT_TIER,
    featuresTitle: 'Data-Driven plan includes:',
    features: [
      { text: 'Unlimited one-click Boost Performance' },
      { text: '100+ performance-driven templates' },
      { text: 'Blog wizard' },
      { text: 'Predictive performance scores & analytics' },
      { text: 'High-performing talking point suggestions' },
      { text: '3 tones of voice' },
      { text: '5 target audiences' },
      { text: '10 messaging bank assets' },
      { text: '10 custom copywriting formulas' },
      { text: '25 brand rules' },
      { text: 'Workspaces', calcFunction: getWorkspacesByTier },
    ],
    featureGroups: [
      {
        features: [
          { text: 'Includes ', calcFunction: getSeatsLabelByTier },
          'Unlimited 1-Click Boost Performance & \nContent Improver',
          '100+ marketing templates',
          'Predictive performance scores & analytics',
          'Blog Wizard with plagiarism checker',
          'Train AI on your brand voice',
          'High-performing talking points',
          'Use Anyword on ChatGPT, Google Docs,\nNotion & more with Google Chrome extension',
        ],
      },
    ],
  },

  [SUBSCRIPTION_PLAN.business_self_serve]: {
    classNamePrefix: 'PricingBusiness',
    label: 'Business',
    description:
      'For marketing teams who want better performance across channels with AI trained on your best messaging & past campaigns.',
    realtimeScoringAndAnalytics: DETAILS_TABLE_ICONS.check,
    creditsPerMonth: 'Unlimited',
    scoreOptimization: DETAILS_TABLE_ICONS.check,
    seats: 'Multiple',
    blogPosts: DETAILS_TABLE_ICONS.check,
    aiWritingTools: DETAILS_TABLE_ICONS.check,
    social: DETAILS_TABLE_ICONS.check,
    ads: DETAILS_TABLE_ICONS.check,
    customAiModels: DETAILS_TABLE_ICONS.minus,
    importAdsFromSocial: DETAILS_TABLE_ICONS.minus,
    copyVariationsInformed: DETAILS_TABLE_ICONS.minus,
    apiAccess: DETAILS_TABLE_ICONS.minus,
    landingPages: 'Write from scratch',
    subjectLines: DETAILS_TABLE_ICONS.check,
    productListings: DETAILS_TABLE_ICONS.check,
    support: 'Chat and ticket\nsupport',
    policyComplianceDetection: DETAILS_TABLE_ICONS.minus,
    tiers: SUBSCRIPTION_SEAT_TIER,
    featuresTitle: 'Everything In Data-Driven, plus:',
    features: [
      { text: 'Connect your marketing channels with unlimited rows (copy assets)' },
      { text: 'Unlimited access to Copy Intelligence with past performance copy analytics' },
      { text: 'Rank your generated content against your existing content' },
      { text: 'Predictive performance scores & analytics' },
      { text: 'Automated website messaging A/B testing' },
      { text: 'Extended brand voice capabilities' },
      { text: 'High-performing talking points' },
      { text: 'Unlimited 1-Click Boost Performance & Content Improver' },
      { text: '100+ marketing templates' },
      { text: 'Predictive performance scores & analytics' },
      { text: 'Blog Wizard with plagiarism checker' },
      { text: 'Use Anyword on ChatGPT, Google Docs, Notion & more with Google Chrome extension' },
      { text: 'Dedicated onboarding & account setup' },
    ],
    featureGroups: [
      {
        features: [
          { text: 'Seats', calcFunction: getSeatsLabelByTier },
          'Connect your marketing channels with \nunlimited rows (copy assets)',
          'Unlimited access to Copy Intelligence with\npast performance copy analytics',
          'Rank your generated content against your existing content',
          'Automated website messaging A/B testing',
          'Extended brand voice capabilities',
          'Onboarding & account setup',
        ],
      },
    ],
  },

  [SUBSCRIPTION_PLAN.business]: {
    label: 'Enterprise',
    description:
      'For teams who want custom-built AI tailored to their brand, with enterprise-grade security, privacy, and control.',
    featuresTitle: 'Everything In Business, plus:',
    featureGroups: [
      {
        features: [
          'Enterprise-grade security & SSO ',
          'Private language model ',
          'Custom marketing channel integrations',
          'Custom-built AI models',
          'Full API access',
          'Admin & role management',
          'Security & legal review',
          'Unlimited workspaces',
          'Priority support',
          'Dedicated customer success manager',
        ],
      },
    ],
  },

  // Obsolete plans
  [SUBSCRIPTION_PLAN.dataDriven100]: {
    label: 'Data-Driven',
  },

  [SUBSCRIPTION_PLAN.tiered]: {
    label: 'Starter',
  },

  [SUBSCRIPTION_PLAN.acquisition]: {
    label: 'Acquire',
  },

  [SUBSCRIPTION_PLAN.conversion]: {
    label: 'Convert',
  },

  [SUBSCRIPTION_PLAN.engagement]: {
    label: 'Engage',
  },
};

export const PLAN_DETAILS_TABLE_ROWS = [
  {
    key: 'creditsPerMonth',
    label: 'Word credits per month',
  },
  {
    key: 'realtimeScoringAndAnalytics',
    label: 'Real-time scoring and analytics',
    subLabel: 'Get real-time scoring and analytics for every copy edit you make',
  },
  {
    key: 'scoreOptimization',
    label: 'Score optimization',
    subLabel: 'Have Anyword rewrite any existing copy to improve its predictive performance score',
  },
  {
    key: 'seats',
    label: 'Seats',
  },
  {
    key: 'blogPosts',
    label: 'Blog posts',
  },
  {
    key: 'aiWritingTools',
    label: 'AI writing tools',
  },
  {
    key: 'social',
    label: 'Social',
    subLabel: 'Channel-specific data-driven copywriting optimized for performance',
  },
  {
    key: 'ads',
    label: 'Ads',
    subLabel: 'Channel-specific data-driven copywriting optimized for performance',
  },
  {
    key: 'landingPages',
    label: 'Landing pages',
  },
  {
    key: 'subjectLines',
    label: 'Subject lines, cold emails,\nProspecting emails',
  },
  {
    key: 'productListings',
    label: 'Product Listings',
    subLabel: 'Amazon, Shopify, Walmart',
  },
  {
    key: 'customAiModels',
    label: 'Custom AI models',
  },
  {
    key: 'policyComplianceDetection',
    label: 'Ad policy compliance\ndetection',
  },
  {
    key: 'importAdsFromSocial',
    label: 'Import your ads from\nFacebook & Google',
  },
  {
    key: 'copyVariationsInformed',
    label: 'Copy variations informed\nby customer reviews',
  },
  {
    key: 'apiAccess',
    label: 'API access',
  },
  {
    key: 'support',
    label: 'Support',
  },
];

export const PLAN_DETAILS_TABLE = [
  {
    type: 'header',
    text: 'Plans Include',
  },
  {
    text: 'Words',
    data: ['Unlimited', 'Unlimited', 'Unlimited', 'Unlimited'],
  },
  {
    text: 'Seats included',
    data: ['1', '3', '3', 'Custom'],
  },
  {
    text: 'Price per additional seat',
    data: [
      '-',
      '$59/mo or $49/mo yearly, max 10 seats',
      '$59/mo or $49/mo yearly, max 10 seats',
      'Custom',
    ],
  },
  {
    text: 'Workspaces',
    data: [1, 5, 10, 'Custom'],
  },
  {
    type: 'header',
    text: 'Copy Generation Tools',
  },
  {
    text: 'Data-Driven Editor',
    data: [<CheckmarkIcon />, <CheckmarkIcon />, <CheckmarkIcon />, <CheckmarkIcon />],
  },
  {
    text: '100+ performance-driven templates',
    data: [<CheckmarkIcon />, <CheckmarkIcon />, <CheckmarkIcon />, <CheckmarkIcon />],
  },
  {
    text: 'Saved prompts',
    data: ['10', '10', '50', 'Unlimited'],
  },
  {
    text: 'Blog Wizard',
    data: [<CheckmarkIcon />, <CheckmarkIcon />, <CheckmarkIcon />, <CheckmarkIcon />],
  },
  {
    text: 'Plagiarism Checker',
    data: ['25/mo', '25/mo', 'Unlimited', 'Unlimited'],
  },
  {
    text: '30+ Languages',
    data: [<CheckmarkIcon />, <CheckmarkIcon />, <CheckmarkIcon />, <CheckmarkIcon />],
  },
  {
    text: 'Grammarly Integration',
    data: [<CheckmarkIcon />, <CheckmarkIcon />, <CheckmarkIcon />, <CheckmarkIcon />],
  },
  {
    text: 'Google Chrome extension',
    data: [<CheckmarkIcon />, <CheckmarkIcon />, <CheckmarkIcon />, <CheckmarkIcon />],
  },
  {
    type: 'header',
    text: 'Copy Intelligence',
  },
  {
    text: 'Custom built AI models',
    data: ['-', '-', '-', 'Unlimited'],
  },
  {
    text: 'Rows (copy assets)',
    data: ['50', '50', 'Unlimited', 'Unlimited'],
  },
  {
    text: '1-click Boost Performance',
    data: ['5/mo', 'Unlimited', 'Unlimited', 'Unlimited'],
  },
  {
    text: 'Predictive performance scores & insights',
    data: [<CheckmarkIcon />, <CheckmarkIcon />, <CheckmarkIcon />, <CheckmarkIcon />],
  },
  {
    text: 'High-performing talking points',
    data: [<CheckmarkIcon />, <CheckmarkIcon />, <CheckmarkIcon />, <CheckmarkIcon />],
  },
  {
    text: 'Automated website messaging',
    data: ['-', '-', <CheckmarkIcon />, <CheckmarkIcon />],
  },
  {
    text: 'Rank your generated content\nagainst your existing content',
    data: ['-', '-', <CheckmarkIcon />, <CheckmarkIcon />],
  },
  {
    text: 'Out of the box integrations',
    data: ['-', '-', <CheckmarkIcon />, <CheckmarkIcon />],
  },
  {
    text: 'Custom integrations',
    data: ['-', '-', '-', <CheckmarkIcon />],
  },
  {
    text: 'Copy analytics',
    data: ['-', '-', <CheckmarkIcon />, <CheckmarkIcon />],
  },
  {
    type: 'header',
    text: 'Brand Voice',
  },
  {
    text: 'Tones of voice',
    data: ['3', '3', '15', 'Unlimited'],
  },
  {
    text: 'Target audiences',
    data: ['5', '5', '25', 'Unlimited'],
  },
  {
    text: 'Messaging bank assets',
    data: ['10', '10', '50', 'Unlimited'],
  },
  {
    text: 'Custom formulas',
    data: ['10', '10', '50', 'Unlimited'],
  },
  {
    text: 'Brand rules',
    data: ['25', '25', '125', 'Unlimited'],
  },
  {
    type: 'header',
    text: 'Admin, Support & Security',
  },
  {
    text: 'Private language model',
    data: ['-', '-', '-', <CheckmarkIcon />],
  },
  {
    text: 'API',
    data: ['-', '-', '-', <CheckmarkIcon />],
  },
  {
    text: 'Dedicated customer success',
    data: ['-', '-', '-', <CheckmarkIcon />],
  },
  {
    text: 'Onboarding & setup',
    data: ['-', '-', <CheckmarkIcon />, <CheckmarkIcon />],
  },
  {
    text: 'SSO',
    data: ['-', '-', '-', <CheckmarkIcon />],
  },
  {
    text: 'Reliability with 99.99% uptime',
    data: [<CheckmarkIcon />, <CheckmarkIcon />, <CheckmarkIcon />, <CheckmarkIcon />],
  },
  {
    text: 'Knowledge base & email support',
    data: [<CheckmarkIcon />, <CheckmarkIcon />, <CheckmarkIcon />, <CheckmarkIcon />],
  },
  {
    text: 'Admin & role management',
    data: ['-', '-', '-', <CheckmarkIcon />],
  },
  {
    text: 'Security review',
    data: ['-', '-', '-', <CheckmarkIcon />],
  },
  {
    text: 'Legal review',
    data: ['-', '-', '-', <CheckmarkIcon />],
  },
];

// functions

export const getPlanProperty = (plan, property, defaultValue = undefined) => {
  const props = PLAN_PROPS[plan];
  return (props && props[property]) || defaultValue;
};

export const getPlanFeatureGroups = (plan) => getPlanProperty(plan, 'featureGroups', []);

export const getSubscriptionPlanLabel = (plan) => getPlanProperty(plan, 'label', plan || '');

export const getSubscriptionPlanSubtitle = (plan) => getPlanProperty(plan, 'subtitle', '');

export const getSubscriptionPlanDescription = (plan) => getPlanProperty(plan, 'description', '');

export const getSubscriptionPlanWordsLimit = (plan) => getPlanProperty(plan, 'wordsLimit', 0);

export const getSubscriptionPlanFeatures = (plan) => getPlanProperty(plan, 'features', []);
export const getSubscriptionPlanFeaturesTitle = (plan) =>
  getPlanProperty(plan, 'featuresTitle', []);

export const getSubscriptionPlanTiers = (plan, defaultValue) =>
  getPlanProperty(plan, 'tiers', defaultValue);

export const getSubscriptionDetails = (plan, key) => getPlanProperty(plan, key, '');
